/**
 * SideBySideView — left v1 / right v2, change-anchored two-column view.
 *
 * Each row in the layout maps to one item from `buildAlignedRows`:
 *
 *   equal        → both columns render the same paragraph plainly
 *   addition     → right column gets a green ChangeMarker, left column is a
 *                  visual gap (matches the right's height via grid stretch)
 *   deletion     → mirror of addition
 *   modification → left column shows contentA in an amber ChangeMarker;
 *                  right shows contentB. If `inlineDiff` is provided we
 *                  render token-level highlights on each side.
 *   move         → both columns render the same content with a blue
 *                  ChangeMarker (positions differ — the row position in
 *                  this view shows where v2 ended up).
 *
 * Layout architecture (P1-1b single-grid refactor, 2026-07-02):
 *   The view uses ONE scroll container with paired-row grids. Each row is
 *   a 2-column CSS grid line — both cells in a row are stretched to the
 *   row's max height by the grid's default `align-items: stretch`. This
 *   eliminates all per-row drift sources by construction:
 *
 *     - Variable annotation thread height (collapsed vs expanded)
 *     - Addition / deletion Gap vs real content height
 *     - pageBreak on one side, paragraph on the other
 *     - modification rows where contentA and contentB lengths differ
 *
 *   The previous two-ColumnViewport-with-sync-scroll design required a
 *   complex RowHeightContext + ResizeObserver infrastructure to
 *   compensate after the fact, and still couldn't handle rows where both
 *   cells have non-null but differently-sized content. The single-grid
 *   layout makes the alignment structural — CSS handles it in one pass
 *   during layout, with no runtime measurement or feedback loops.
 *
 *   Annotation threads (when `annotations` is supplied) span both columns
 *   below the row's cells — they don't bias one column's height over the
 *   other because they're not inside either column.
 *
 * DOM contract: the `change-row` / `data-change-id` markers are emitted
 * ONLY on side B's cell. Existing e2e and perf scripts query
 * `[data-change-id]` via `.first()` / `.count()`; emitting it on both
 * sides would double their counts and break the assertions. Side B also
 * hosts the accept/reject action UI, so it's the natural home for those
 * markers.
 */
import { memo } from 'react';

import { AnnotationThread } from './AnnotationThread';
import {
  buildAlignedRows,
  type AlignedRow,
  type RowKind,
} from './alignment';
import { ChangeMarker, type DecisionDisplay } from './ChangeMarker';
import { DecidableChange, displayModeFor, type DecisionRole } from './DecidableChange';
import { InlineDiffView } from './inlineDiff';
import { NodeBlock } from './NodeBlock';
import { UnchangedBlock } from './UnchangedBlock';
import type { DecisionAdapter } from './decisionAdapter';
import type { NavigationAdapter } from './navigationAdapter';
import type { UseAnnotationsResult } from './useAnnotations';
import type { UseFocusReadModeResult } from './useFocusReadMode';
import type { ChangeResponse, ChangeType } from '../../lib/veridoc-api';
import type { DocNode } from '../doc-viewer/DocViewer.types';

type RowSide = 'A' | 'B';

interface SideBySideViewProps {
  treeA: DocNode;
  treeB: DocNode;
  changes: ChangeResponse[];
  /** Optional review-decision adapter — when omitted (e.g. inside Storybook),
   * change blocks render without overlays. */
  decisions?: DecisionAdapter;
  /** Optional navigation adapter — propagates DOM node registration + selected
   *  state into each change wrapper. */
  navigation?: NavigationAdapter;
  /** Optional focus-mode adapter — when supplied and enabled, unchanged
   *  paragraphs dim to 30 % opacity until clicked (M3 Phase 3.5). */
  focus?: UseFocusReadModeResult;
  /** Optional annotation hook — when supplied, each change row gets an
   *  inline thread spanning both columns (M4 Phase 4.4). */
  annotations?: UseAnnotationsResult;
  /** 左栏（v1 / 基线侧）标题。宿主传入版本标签或文档名替换默认占位
   *  文案；不传时显示「基准版本 A」（demo 行为）。 */
  labelA?: string;
  /** 右栏（v2 / 对比侧）标题。同 `labelA`，默认「对比版本 B」。 */
  labelB?: string;
}

export function SideBySideView({
  treeA,
  treeB,
  changes,
  decisions,
  navigation,
  focus,
  annotations,
  labelA = '基准版本 A',
  labelB = '对比版本 B',
}: SideBySideViewProps): React.JSX.Element {
  if (treeA.type !== 'doc' || treeB.type !== 'doc') {
    return <ViewError message="预期 doc 根节点，但收到了非法输入" />;
  }

  const rows = buildAlignedRows(treeA.children, treeB.children, changes);

  return (
    <div
      className="max-h-[70vh] overflow-y-auto pr-1"
      data-testid="side-by-side-viewport"
    >
      {/* Sticky header pair — first grid line, both column headers stick
          together at the top of the scroll area. */}
      <div className="grid grid-cols-2 gap-x-4 sticky top-0 z-10">
        <ColumnHeader label={labelA} />
        <ColumnHeader label={labelB} />
      </div>
      {rows.map((row) => (
        <PairedRow
          key={row.key}
          row={row}
          decisions={decisions}
          navigation={navigation}
          focus={focus}
          annotations={annotations}
        />
      ))}
    </div>
  );
}

/**
 * PairedRow — one logical row rendered as a 2-column grid line.
 *
 * Both cells live in the same CSS grid, so `align-items: stretch` (the
 * default) pulls the shorter cell up to the taller cell's height. This is
 * what eliminates per-row drift structurally — no JavaScript measurement,
 * no feedback loops, no ResizeObserver.
 *
 * The annotation thread (when applicable) renders INSIDE side B's SideCell,
 * directly below the cell content — never as a separate full-width row.
 * This keeps the thread visually anchored under the right (v2) column
 * where the change was tracked, matching the existing DOM contract that
 * `data-testid="annotation-thread"` lives in side B's subtree. CSS grid
 * stretch handles the alignment: side A's cell stretches to match side
 * B's (cell + thread) height, so the next row stays aligned.
 *
 * Thread spacing: `-mt-3` pulls the thread up into the cell content's
 * natural bottom margin (paragraph `mb-4` etc.) so the thread sits close
 * to the paragraph it annotates; `mb-4` keeps a normal paragraph-gap
 * before the next row.
 */
const PairedRow = memo(function PairedRow({
  row,
  decisions,
  navigation,
  focus,
  annotations,
}: {
  row: AlignedRow;
  decisions?: DecisionAdapter;
  navigation?: NavigationAdapter;
  focus?: UseFocusReadModeResult;
  annotations?: UseAnnotationsResult;
}): React.JSX.Element {
  const hasThread = !!row.change && !!annotations;
  return (
    <div className="grid grid-cols-2 gap-x-4" data-row-key={row.key}>
      <SideCell>
        <RowRenderer
          row={row}
          side="A"
          decisions={decisions}
          navigation={navigation}
          focus={focus}
        />
      </SideCell>
      <SideCell
        data-testid={row.change ? 'change-row' : undefined}
        data-change-id={row.change?.id}
      >
        <RowRenderer
          row={row}
          side="B"
          decisions={decisions}
          navigation={navigation}
          focus={focus}
        />
        {hasThread && (
          <div className="-mt-3 mb-4">
            <AnnotationThread
              changeId={row.change!.id}
              annotations={annotations!}
            />
          </div>
        )}
      </SideCell>
    </div>
  );
});

function ColumnHeader({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="-mx-1 mb-2 border-b border-ink-200 bg-ink-0/95 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500 backdrop-blur">
      {label}
    </div>
  );
}

const RowRenderer = memo(function RowRenderer({
  row,
  side,
  decisions,
  navigation,
  focus,
}: {
  row: AlignedRow;
  side: RowSide;
  decisions?: DecisionAdapter;
  navigation?: NavigationAdapter;
  focus?: UseFocusReadModeResult;
}): React.JSX.Element {
  // Helper closure — wraps a child marker tree with the decision overlay
  // *if* this row is backed by a change record AND an adapter was supplied.
  // The closure exposes the resolved `decisionDisplay` to the render
  // callback so the inner ChangeMarker can repaint itself winner/loser
  // (M3 B5: "I accepted but the block is still yellow"). `role` decides
  // which side this cell represents — see DecidableChange.DecisionRole.
  // ``interactive=false`` suppresses the hover accept/reject overlay while
  // keeping the chip + outline + navigation registration. Used on the
  // left mirror cell of modification / move rows so action lives only on
  // the right (M3 B4).
  const wrap = (
    renderInner: (display: DecisionDisplay) => React.JSX.Element,
    opts: { changeType: ChangeType; role: DecisionRole; interactive?: boolean },
  ): React.JSX.Element => {
    if (!row.change || !decisions) {
      return renderInner('pending');
    }
    const status = decisions.getStatus(row.change.id);
    const display = displayModeFor(status, opts.role);
    return (
      <DecidableChange
        changeId={row.change.id}
        changeType={opts.changeType}
        status={status}
        meta={decisions.getMeta(row.change.id)}
        pending={decisions.isPending(row.change.id)}
        canDecide={decisions.canDecide}
        onDecide={(action, note) => decisions.decide(row.change!.id, action, note)}
        navigation={navigation}
        interactive={opts.interactive !== false}
        role={opts.role}
      >
        {renderInner(display)}
      </DecidableChange>
    );
  };

  switch (row.kind) {
    case 'equal':
      return side === 'A' ? (
        row.leftBlock ? (
          <UnchangedBlock
            nodeKey={`${row.key}-a`}
            enabled={focus?.enabled ?? false}
            dimmed={focus?.isTempActive(`${row.key}-a`) ?? false}
            tempActivate={focus?.tempActivate}
          >
            <NodeBlock node={row.leftBlock} />
          </UnchangedBlock>
        ) : (
          <Gap />
        )
      ) : row.rightBlock ? (
        <UnchangedBlock
          nodeKey={`${row.key}-b`}
          enabled={focus?.enabled ?? false}
          dimmed={focus?.isTempActive(`${row.key}-b`) ?? false}
          tempActivate={focus?.tempActivate}
        >
          <NodeBlock node={row.rightBlock} />
        </UnchangedBlock>
      ) : (
        <Gap />
      );

    case 'addition':
      return side === 'A' ? (
        <Gap kind={row.kind} />
      ) : (
        wrap(
          (display) => (
            <ChangeMarker changeType="addition" decisionDisplay={display}>
              {row.rightBlock && <NodeBlock node={row.rightBlock} />}
            </ChangeMarker>
          ),
          { changeType: 'addition', role: 'B' },
        )
      );

    case 'deletion':
      return side === 'A' ? (
        wrap(
          (display) => (
            <ChangeMarker changeType="deletion" decisionDisplay={display}>
              {row.leftBlock && <NodeBlock node={row.leftBlock} />}
            </ChangeMarker>
          ),
          { changeType: 'deletion', role: 'A' },
        )
      ) : (
        <Gap kind={row.kind} />
      );

    case 'modification': {
      const inline = row.change?.inlineDiff;
      return side === 'A' ? (
        wrap(
          (display) => (
            <ChangeMarker changeType="modification" decisionDisplay={display}>
              {inline && inline.length > 0 ? (
                <p className="font-serif text-md leading-relaxed">
                  <InlineDiffView segments={inline} side="a" />
                </p>
              ) : row.leftBlock ? (
                <NodeBlock node={row.leftBlock} />
              ) : null}
            </ChangeMarker>
          ),
          { changeType: 'modification', role: 'A', interactive: false },
        )
      ) : (
        wrap(
          (display) => (
            <ChangeMarker changeType="modification" decisionDisplay={display}>
              {inline && inline.length > 0 ? (
                <p className="font-serif text-md leading-relaxed">
                  <InlineDiffView segments={inline} side="b" />
                </p>
              ) : row.rightBlock ? (
                <NodeBlock node={row.rightBlock} />
              ) : null}
            </ChangeMarker>
          ),
          { changeType: 'modification', role: 'B' },
        )
      );
    }

    case 'move':
      return side === 'A' ? (
        wrap(
          (display) => (
            <ChangeMarker changeType="move" decisionDisplay={display}>
              {row.leftBlock && <NodeBlock node={row.leftBlock} />}
            </ChangeMarker>
          ),
          { changeType: 'move', role: 'A', interactive: false },
        )
      ) : (
        wrap(
          (display) => (
            <ChangeMarker changeType="move" decisionDisplay={display}>
              {row.rightBlock && <NodeBlock node={row.rightBlock} />}
            </ChangeMarker>
          ),
          { changeType: 'move', role: 'B' },
        )
      );

    case 'format': {
      // Format changes: identical text, different bold/italic. Show inline diff
      // on both sides so the reviewer can see which runs changed style.
      const inline = row.change?.inlineDiff;
      return side === 'A' ? (
        wrap(
          (display) => (
            <ChangeMarker changeType="format" decisionDisplay={display}>
              {inline && inline.length > 0 ? (
                <p className="font-serif text-md leading-relaxed">
                  <InlineDiffView segments={inline} side="a" />
                </p>
              ) : row.leftBlock ? (
                <NodeBlock node={row.leftBlock} />
              ) : null}
            </ChangeMarker>
          ),
          { changeType: 'format', role: 'A', interactive: false },
        )
      ) : (
        wrap(
          (display) => (
            <ChangeMarker changeType="format" decisionDisplay={display}>
              {inline && inline.length > 0 ? (
                <p className="font-serif text-md leading-relaxed">
                  <InlineDiffView segments={inline} side="b" />
                </p>
              ) : row.rightBlock ? (
                <NodeBlock node={row.rightBlock} />
              ) : null}
            </ChangeMarker>
          ),
          { changeType: 'format', role: 'B' },
        )
      );
    }

    default: {
      const _exhaustive: never = row.kind;
      void _exhaustive;
      return <></>;
    }
  }
});

/**
 * SideCell — one half of a paired row.
 *
 * Lives inside a 2-column CSS grid line. The grid's default `align-items:
 * stretch` makes every SideCell in a row the same height (the row's max
 * content height) — this is what keeps the two columns visually aligned
 * per row without any JavaScript measurement.
 *
 * Extra props (e.g. `data-testid`, `data-change-id`) pass through to the
 * underlying div so the DOM contract for side-B change markers stays
 * intact.
 */
const SideCell = memo(function SideCell({
  children,
  ...rest
}: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div className="min-w-0" {...rest}>
      {children}
    </div>
  );
});

function Gap({ kind }: { kind?: RowKind }): React.JSX.Element {
  // Visual gap for the empty side of a pure addition/deletion. Matches the
  // §3.2 spec: ink-50 background, dashed mid-line when tall, no border.
  // Lives inside a SideCell whose height is driven by the row's other
  // column (via CSS grid stretch); `h-full` makes the gap fill that
  // height so the coloured background extends through the row.
  const isChangeGap = kind && kind !== 'equal';
  return (
    <div
      aria-hidden="true"
      className={`mb-4 min-h-12 h-full rounded-sm ${
        isChangeGap ? 'bg-ink-50' : ''
      } flex items-center justify-center`}
    >
      {isChangeGap && (
        <span className="block w-full border-t border-dashed border-ink-200" />
      )}
    </div>
  );
}

function ViewError({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="rounded-md border border-semantic-danger bg-semantic-dangerBg p-4 text-sm text-ink-900">
      {message}
    </div>
  );
}

export default SideBySideView;
